import { forwardRef, type SelectHTMLAttributes } from "react";

type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, helperText, options, placeholder, className = "", id, ...props },
  ref,
) {
  const selectId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1 block font-medium text-gray-700 text-sm" htmlFor={selectId}>
          {label}
        </label>
      )}
      <select
        className={`w-full rounded-md border bg-white px-3 py-2 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 ${
          error
            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
        } ${className}`}
        id={selectId}
        ref={ref}
        {...props}
      >
        {placeholder && (
          <option disabled value="">
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option disabled={option.disabled} key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {helperText && !error && <p className="mt-1 text-gray-500 text-sm">{helperText}</p>}
      {error && <p className="mt-1 text-red-600 text-sm">{error}</p>}
    </div>
  );
});

export type { SelectOption };
